/* Chord symbols, qualities and names. */
import { type ChordQuality, type ChordRef, isQuality, QUAL } from '../dsp/chord';
import { LETTER_PC, mod, PC_FLAT, PC_SHARP, pretty, solfege } from './notes';

export { type ChordQuality, type ChordRef, QUAL };

/** Root spellings accepted in chord symbols. */
export type RootName =
  | 'C'
  | 'C#'
  | 'Db'
  | 'D'
  | 'D#'
  | 'Eb'
  | 'E'
  | 'F'
  | 'F#'
  | 'Gb'
  | 'G'
  | 'G#'
  | 'Ab'
  | 'A'
  | 'A#'
  | 'Bb'
  | 'B';

/** A chord symbol the app understands, e.g. "Am", "F#m7", "Bbmaj7" (checked at compile time). */
export type ChordName = `${RootName}${ChordQuality}`;

export const QUAL_NAME: Record<ChordQuality, string> = {
  '': 'majör',
  m: 'minör',
  '7': "dominant 7'li",
  maj7: "majör 7'li",
  m7: "minör 7'li",
  dim: 'eksik (dim)',
  aug: 'artık (aug)',
  sus2: 'sus2',
  sus4: 'sus4',
  m7b5: 'yarı eksik (m7♭5)',
  '6': "6'lı",
  m6: "minör 6'lı",
  '5': 'power chord (5)',
  dim7: "eksik 7'li",
  add9: 'add9',
};

export interface ParsedChord extends ChordRef {
  flat: boolean;
  name: string;
}

/** "F#m7" -> { root: 6, q: 'm7', flat: false, name: 'F#m7' }; null if unknown. */
export function parseChord(s: string): ParsedChord | null {
  const m = /^([A-G])(#|b)?(.*)$/.exec(String(s).trim());
  if (!m) return null;
  const q = m[3] as string;
  if (!isQuality(q)) return null;
  const root = mod((LETTER_PC[m[1] as string] as number) + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0), 12);
  return { root, q, flat: m[2] === 'b', name: (m[1] as string) + (m[2] || '') + q };
}

// Conventional guitar spellings when there is no key context
const MAJ_SPELL = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
const MIN_SPELL = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'G#', 'A', 'Bb', 'B'];
const MINORISH = new Set<ChordQuality>(['m', 'm7', 'm6', 'dim', 'm7b5']);

/** Chord symbol for a root and quality. `flat` forces the spelling; undefined picks the usual one. */
export function chordSymbol(root: number, q: ChordQuality, flat?: boolean): string {
  let r: string;
  if (flat === true) r = PC_FLAT[mod(root, 12)] as string;
  else if (flat === false) r = PC_SHARP[mod(root, 12)] as string;
  else r = (MINORISH.has(q) ? MIN_SPELL : MAJ_SPELL)[mod(root, 12)] as string;
  return r + q;
}

export const chordPcs = (root: number, q: ChordQuality): number[] => QUAL[q].map((i) => mod(root + i, 12));

/** "Re minör", "Si♭ dominant 7'li"... */
export function chordLongName(root: number, q: ChordQuality, flat?: boolean): string {
  const sym = chordSymbol(root, q, flat);
  const rootName = pretty(solfege(root, flat != null ? flat : /b/.test(sym.slice(1, 2))));
  return `${rootName} ${QUAL_NAME[q] || q}`;
}

export const sameChord = (a: ChordRef, b: ChordRef): boolean => a.root === b.root && a.q === b.q;
